import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { VariablePill } from './tiptapVariables';

// Mini-edytor TipTap do pól emaila faktury: zmienne jako PIGUŁKI (przyjazne nazwy),
// wstawiane w miejscu kursora — bez surowych {{token}} widocznych dla użytkownika.
// Magazyn = zwykły string z {{token}} (backend podstawia wartości); pigułka to tylko warstwa wizualna.

// String "{{token}} tekst" → HTML wejściowy edytora (pigułki + akapity z \n).
function tokensToHtml(value) {
  const esc = String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const withPills = esc.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => `<span data-variable="${name}">{{${name}}}</span>`);
  return withPills
    .split(/\n/)
    .map((line) => `<p>${line || ''}</p>`)
    .join('') || '<p></p>';
}

const Wrapper = styled.div`
  .ProseMirror {
    min-height: ${({ $singleLine }) => ($singleLine ? '20px' : '120px')};
    padding: var(--sp-3) var(--sp-3);
    border: 1px solid var(--input-border);
    border-radius: var(--r-sm);
    outline: none;
    font-size: var(--fs-base);
    line-height: 1.5;
    color: var(--text);
    background: var(--input-bg);
  }
  .ProseMirror:focus { border-color: var(--primary); }
  .ProseMirror p { margin: 0; }
  .ProseMirror p + p { margin-top: var(--sp-1); }
  .tt-variable {
    display: inline-block;
    background: var(--info-bg);
    border: 1px solid var(--info-border);
    border-radius: var(--r-sm);
    padding: 0 7px;
    margin: 0 1px;
    font-size: 0.85em;
    color: var(--info);
    font-weight: 600;
    white-space: nowrap;
    user-select: none;
  }
`;

// `ariaLabel` (opcjonalny): nazwa dostępna edytora. Napis nad polem bywa zwykłym <span> (edytor
// TipTap nie jest elementem formularza, więc <label for> go nie nazywa) i czytnik ekranu
// ogłaszał pole tekstowe bez nazwy (test odbiorczy konstruktora oferty 11.10.2026).
function EmailPillField({ value, labelFor, onChange, onFocusEditor, registerEditor, singleLine, ariaLabel }) {
  const lastRef = useRef(value);

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: false, bulletList: false, orderedList: false, blockquote: false,
      codeBlock: false, horizontalRule: false,
    }),
    VariablePill.configure({ labelFor: labelFor || ((n) => `{{${n}}}`) }),
  ], [labelFor]);

  const editor = useEditor({
    extensions,
    content: tokensToHtml(value),
    editorProps: {
      ...(ariaLabel ? { attributes: { 'aria-label': ariaLabel, 'aria-multiline': singleLine ? 'false' : 'true' } } : {}),
      ...(singleLine ? {
        // Temat = jedna linia: blokuj Enter.
        handleKeyDown: (_view, event) => event.key === 'Enter',
      } : {}),
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText({ blockSeparator: '\n' });
      lastRef.current = text;
      if (onChange) onChange(text);
    },
    onFocus: ({ editor }) => { if (onFocusEditor) onFocusEditor(editor); },
  });

  useEffect(() => {
    if (!editor || !registerEditor) return undefined;
    registerEditor(editor);
    return () => registerEditor(null);
  }, [editor, registerEditor]);

  // Synchronizacja zewnętrznych zmian (load) → edytor.
  useEffect(() => {
    if (!editor) return;
    if (value !== lastRef.current) {
      lastRef.current = value;
      editor.commands.setContent(tokensToHtml(value), false);
    }
  }, [value, editor]);

  if (!editor) return <Wrapper $singleLine={singleLine}><div className="ProseMirror" /></Wrapper>;

  return (
    <Wrapper $singleLine={singleLine}>
      <EditorContent editor={editor} />
    </Wrapper>
  );
}

export default EmailPillField;
